<script setup lang="ts">
import { useDivisibleReceiver } from '@precon-tools-suite-by-shared/vue-divisibles';
import { usePartialComponent } from '@precon-tools-suite-by-shared/vue-partials';
import Skeleton from 'primevue/skeleton';
import Button from 'primevue/button';
import { type Component, computed } from 'vue';

const props = withDefaults(
    defineProps<{
        partial: string | Component;
        fallbackPartial?: string | Component | null;
        partialProps?: Record<string, unknown>;
    }>(),
    {
        fallbackPartial: null,
        partialProps: () => ({}),
    }
);

const receiver = useDivisibleReceiver();
const partialComponent = usePartialComponent(() => props.partial, {
    fallback: () => props.fallbackPartial,
});

const header = receiver.use('header');
const title = receiver.use('title');
const subtitle = receiver.use('subtitle');
const description = receiver.use('description');
const tools = receiver.use('tools');
const footer = receiver.use('footer');
const actions = receiver.use('actions');

const showHeader = computed<boolean>(
    () =>
        header.exists.value ||
        title.exists.value ||
        subtitle.exists.value ||
        tools.exists.value
);
</script>

<template>
    <article class="card">
        <header v-if="showHeader" id="card-header" class="card-header">
            <template v-if="header.exists.value">
                <header.component />
            </template>
            <template v-else>
                <div class="card-title flex-grow-1">
                    <h5 v-if="title.exists.value">
                        <title.component>
                            <template #skeleton>
                                <Skeleton style="height: 2rem; width: 12rem" />
                            </template>
                        </title.component>
                    </h5>
                    <p
                        v-if="subtitle.exists.value"
                        class="card-subtitle"
                        :style="
                            subtitle.loading.value
                                ? {
                                      'margin-top': '-0.75rem',
                                  }
                                : {}
                        "
                    >
                        <subtitle.component>
                            <template #skeleton>
                                <Skeleton style="height: 1rem; width: 16rem" />
                            </template>
                        </subtitle.component>
                    </p>
                </div>

                <tools.component>
                    <template #skeleton>
                        <Button
                            class="p-button-rounded p-button-text p-button-secondary"
                            icon="pi pi-spinner pi-spin"
                            disabled
                        />
                    </template>
                </tools.component>
            </template>
        </header>

        <p v-if="description.exists.value" class="card-text">
            <description.component>
                <template #skeleton>
                    <Skeleton style="width: 100%; display: inline-block" />
                    <Skeleton style="width: 40%; display: inline-block" />
                </template>
            </description.component>
        </p>

        <component
            :is="partialComponent"
            class="card-body"
            v-bind="partialProps"
        >
            <slot />
        </component>

        <footer
            v-if="footer.exists.value || actions.exists.value"
            class="card-footer flex-row flex flex-wrap justify-content-end gap-2 mt-2"
        >
            <div v-if="footer.exists.value" class="mr-auto text-sm">
                <footer.component>
                    <template #skeleton>
                        <Skeleton
                            style="
                                width: 7rem;
                                max-width: 50%;
                                margin-bottom: 0.25rem;
                            "
                        />
                        <Skeleton style="width: 14rem; max-width: 80%" />
                    </template>
                </footer.component>
            </div>
            <actions.component v-if="actions.exists.value">
                <template #skeleton>
                    <Skeleton style="height: 2.2rem; width: 8rem" />
                    <Skeleton style="height: 2.2rem; width: 6rem" />
                </template>
            </actions.component>
        </footer>
    </article>
</template>

<style lang="scss">
.card-body {
    &.p-message {
        margin-top: 0 !important;
        margin-bottom: 0 !important;
    }
}
</style>
